import React, { Component } from 'react';
import ReactDOM from 'react-dom';
import { parseReactProps } from '~/components/legacy/reactProps.js';
import legacyEventHub from '~/modules/legacyRuntime.js';

class TrackWaveformPlayer extends Component {
  constructor(props) {
    super(props);

    this.handlePlayPauseClick = this.handlePlayPauseClick.bind(this);
    this.handleWaveformClick = this.handleWaveformClick.bind(this);
    this.handleWaveformKeyDown = this.handleWaveformKeyDown.bind(this);
    this.handleUpdateTrackInfo = this.handleUpdateTrackInfo.bind(this);
    this.handleUpdateTrackVariantInfo = this.handleUpdateTrackVariantInfo.bind(this);
    this.handleAudioPlayerWillChangeTrack = this.handleAudioPlayerWillChangeTrack.bind(this);
    this.handleAudioPlayerPlay = this.handleAudioPlayerPlay.bind(this);
    this.handleAudioPlayerTimeUpdate = this.handleAudioPlayerTimeUpdate.bind(this);
    this.handleAudioPlayerStop = this.handleAudioPlayerStop.bind(this);
    this.handleAudioPlayerEnded = this.handleAudioPlayerEnded.bind(this);
    this.unregisterEventHubListeners = this.unregisterEventHubListeners.bind(this);

    this.eventHubListeners = [];
    this.awaitingFreshPlaybackStart = false;
    this.expectedCurrentTimeAfterSeek = null;

    this.state = {
      isActiveTrack: false,
      isPlaying: false,
      currentTime: 0,
      durationSeconds: this.initialDurationSeconds()
    };
  }

  componentDidMount() {
    if (!legacyEventHub || typeof legacyEventHub.on !== 'function') {
      return;
    }

    this.registerEventHubListener('updateTrackInfo', this.handleUpdateTrackInfo);
    this.registerEventHubListener('updateTrackVariantInfo', this.handleUpdateTrackVariantInfo);
    this.registerEventHubListener('audioPlayerWillChangeTrack', this.handleAudioPlayerWillChangeTrack);
    this.registerEventHubListener('audioPlayerPlay', this.handleAudioPlayerPlay);
    this.registerEventHubListener('audioPlayerTimeUpdate', this.handleAudioPlayerTimeUpdate);
    this.registerEventHubListener('audioPlayerStop', this.handleAudioPlayerStop);
    this.registerEventHubListener('audioPlayerEnded', this.handleAudioPlayerEnded);

    this.syncFromGlobalPlayer();
  }

  componentWillUnmount() {
    this.unregisterEventHubListeners();
  }

  initialDurationSeconds() {
    return this.numericValue(this.props.durationSeconds);
  }

  previewTrackData() {
    return {
      id: this.props.trackId,
      title: this.props.title,
      display_title: this.props.title,
      length: this.initialDurationSeconds(),
      length_seconds: this.initialDurationSeconds()
    };
  }

  isVariantPlayer() {
    return Boolean(this.props.trackVariantId);
  }

  playbackContext(eventArgs = []) {
    const inlineContext = eventArgs[3];
    if (inlineContext && typeof inlineContext === 'object') {
      return inlineContext;
    }

    const audioPlayer = window.AudioPlayer || {};
    if (typeof audioPlayer.currentPlaybackContext === 'function') {
      return audioPlayer.currentPlaybackContext() || {};
    }

    return {
      trackData: audioPlayer.currentTrackData,
      trackVariantId: audioPlayer.currentTrackVariantId,
      trackVariantData: audioPlayer.currentTrackVariantData,
      seekRatio: audioPlayer.currentPlaybackSeekRatio ?? null
    };
  }

  isOwnVariant(variantData) {
    return Boolean(variantData) && String(variantData.id) === String(this.props.trackVariantId);
  }

  isOwnPlayback(trackData, playbackContext = {}) {
    if (this.isVariantPlayer()) {
      return String(playbackContext.trackVariantId) === String(this.props.trackVariantId);
    }

    return this.isOwnTrack(trackData) && !playbackContext.trackVariantId;
  }

  syncFromGlobalPlayer() {
    if (!window.AudioPlayer || !window.AudioPlayer.currentTrackData) {
      return;
    }

    const trackData = window.AudioPlayer.currentTrackData;
    const playbackContext = this.playbackContext();

    if (!this.isOwnPlayback(trackData, playbackContext)) {
      return;
    }

    const playable = typeof window.AudioPlayer.playableElement === 'function' ? window.AudioPlayer.playableElement() : null;
    const durationSource = this.isVariantPlayer() ? playbackContext.trackVariantData : trackData;

    this.setState({
      isActiveTrack: true,
      isPlaying: Boolean(window.AudioPlayer.playing),
      currentTime: this.numericValue(playable && playable.currentTime),
      durationSeconds: this.resolveDurationSeconds(durationSource, playable && playable.duration)
    });
  }

  eventArgs(args) {
    if (Array.isArray(args)) {
      return args;
    }

    return typeof args === 'undefined' ? [] : [args];
  }

  registerEventHubListener(eventName, handler) {
    legacyEventHub.on(eventName, handler);
    this.eventHubListeners.push({ eventName: eventName, handler: handler });
  }

  unregisterEventHubListeners() {
    if (!legacyEventHub || typeof legacyEventHub.off !== 'function') {
      this.eventHubListeners = [];
      return;
    }

    this.eventHubListeners.forEach((listener) => {
      legacyEventHub.off(listener.eventName, listener.handler);
    });
    this.eventHubListeners = [];
  }

  numericValue(value) {
    const parsed = parseFloat(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  isOwnTrack(trackData) {
    return Boolean(trackData) && String(trackData.id) === String(this.props.trackId);
  }

  resolveDurationSeconds(trackData, explicitDuration) {
    const explicit = this.numericValue(explicitDuration);
    if (explicit > 0) {
      return explicit;
    }

    const fromTrackLength = this.numericValue(trackData && (trackData.length_seconds || trackData.length));
    if (fromTrackLength > 0) {
      return fromTrackLength;
    }

    return this.initialDurationSeconds();
  }

  resetPlaybackState() {
    this.awaitingFreshPlaybackStart = false;
    this.expectedCurrentTimeAfterSeek = null;
    this.setState({
      isActiveTrack: false,
      isPlaying: false,
      currentTime: 0,
      durationSeconds: this.initialDurationSeconds()
    });
  }

  handleUpdateTrackInfo(args) {
    const eventArgs = this.eventArgs(args);
    const trackData = eventArgs[0];
    const playbackContext = this.playbackContext(eventArgs);
    const hasSeekRatio = typeof playbackContext.seekRatio === 'number';

    if (this.isVariantPlayer()) {
      if (this.state.isActiveTrack || this.state.isPlaying) {
        this.resetPlaybackState();
      }
      return;
    }

    if (!this.isOwnPlayback(trackData, playbackContext)) {
      if (this.state.isActiveTrack || this.state.isPlaying) {
        this.resetPlaybackState();
      }
      return;
    }

    const durationSeconds = this.resolveDurationSeconds(trackData);
    const expectedCurrentTimeAfterSeek = hasSeekRatio && durationSeconds > 0 ? durationSeconds * playbackContext.seekRatio : null;

    this.setState({
      isActiveTrack: true,
      isPlaying: false,
      currentTime: expectedCurrentTimeAfterSeek ?? 0,
      durationSeconds: durationSeconds
    });
    this.awaitingFreshPlaybackStart = !hasSeekRatio;
    this.expectedCurrentTimeAfterSeek = expectedCurrentTimeAfterSeek;
  }

  handleUpdateTrackVariantInfo(args) {
    const eventArgs = this.eventArgs(args);
    const variantData = eventArgs[1];
    const playbackContext = this.playbackContext(eventArgs);
    const hasSeekRatio = typeof playbackContext.seekRatio === 'number';

    if (!this.isVariantPlayer()) {
      if (this.state.isActiveTrack || this.state.isPlaying) {
        this.resetPlaybackState();
      }
      return;
    }

    if (!this.isOwnVariant(variantData)) {
      if (this.state.isActiveTrack || this.state.isPlaying) {
        this.resetPlaybackState();
      }
      return;
    }

    const durationSeconds = this.resolveDurationSeconds(variantData);
    const expectedCurrentTimeAfterSeek = hasSeekRatio && durationSeconds > 0 ? durationSeconds * playbackContext.seekRatio : null;

    this.setState({
      isActiveTrack: true,
      isPlaying: false,
      currentTime: expectedCurrentTimeAfterSeek ?? 0,
      durationSeconds: durationSeconds
    });
    this.awaitingFreshPlaybackStart = !hasSeekRatio;
    this.expectedCurrentTimeAfterSeek = expectedCurrentTimeAfterSeek;
  }

  handleAudioPlayerWillChangeTrack(args) {
    const eventArgs = this.eventArgs(args);
    const payload = eventArgs[0] || {};
    const hasSeekRatio = typeof payload.seekRatio === 'number';

    if (this.isVariantPlayer()) {
      if (String(payload.trackVariantId) !== String(this.props.trackVariantId)) {
        this.resetPlaybackState();
        return;
      }

      const durationSeconds = this.resolveDurationSeconds(payload.trackVariantData || { id: this.props.trackVariantId });
      const expectedCurrentTimeAfterSeek = hasSeekRatio && durationSeconds > 0 ? durationSeconds * payload.seekRatio : null;

      this.setState({
        isActiveTrack: true,
        isPlaying: false,
        currentTime: expectedCurrentTimeAfterSeek ?? 0,
        durationSeconds: durationSeconds
      });
      this.awaitingFreshPlaybackStart = !hasSeekRatio;
      this.expectedCurrentTimeAfterSeek = expectedCurrentTimeAfterSeek;
      return;
    }

    if (String(payload.trackId) !== String(this.props.trackId) || payload.trackVariantId) {
      this.resetPlaybackState();
      return;
    }

    const durationSeconds = this.resolveDurationSeconds(payload.trackData || this.previewTrackData());
    const expectedCurrentTimeAfterSeek = hasSeekRatio && durationSeconds > 0 ? durationSeconds * payload.seekRatio : null;

    this.setState({
      isActiveTrack: true,
      isPlaying: false,
      currentTime: expectedCurrentTimeAfterSeek ?? 0,
      durationSeconds: durationSeconds
    });
    this.awaitingFreshPlaybackStart = !hasSeekRatio;
    this.expectedCurrentTimeAfterSeek = expectedCurrentTimeAfterSeek;
  }

  handleAudioPlayerPlay(args) {
    const eventArgs = this.eventArgs(args);
    const trackData = eventArgs[0];
    const playbackContext = this.playbackContext(eventArgs);
    const durationSource = this.isVariantPlayer() ? playbackContext.trackVariantData : trackData;

    if (!this.isOwnPlayback(trackData, playbackContext)) {
      if (this.state.isActiveTrack || this.state.isPlaying) {
        this.resetPlaybackState();
      }
      return;
    }

    let currentTime = this.numericValue(eventArgs[1]);
    if (this.expectedCurrentTimeAfterSeek !== null) {
      currentTime = this.expectedCurrentTimeAfterSeek;
    }
    if (this.awaitingFreshPlaybackStart && playbackContext.seekRatio === null) {
      currentTime = 0;
    }

    this.setState({
      isActiveTrack: true,
      isPlaying: true,
      currentTime: currentTime,
      durationSeconds: this.resolveDurationSeconds(durationSource, eventArgs[2])
    });
  }

  handleAudioPlayerTimeUpdate(args) {
    const eventArgs = this.eventArgs(args);
    const trackData = eventArgs[0];
    const playbackContext = this.playbackContext(eventArgs);
    const durationSource = this.isVariantPlayer() ? playbackContext.trackVariantData : trackData;

    if (!this.isOwnPlayback(trackData, playbackContext)) {
      return;
    }

    const currentTime = this.numericValue(eventArgs[1]);
    if (this.expectedCurrentTimeAfterSeek !== null) {
      if (Math.abs(currentTime - this.expectedCurrentTimeAfterSeek) > 1) {
        return;
      }

      this.expectedCurrentTimeAfterSeek = null;
      this.awaitingFreshPlaybackStart = false;
    }
    if (this.awaitingFreshPlaybackStart && playbackContext.seekRatio === null) {
      if (currentTime > 1) {
        return;
      }

      this.awaitingFreshPlaybackStart = false;
    }

    this.setState({
      isActiveTrack: true,
      currentTime: currentTime,
      durationSeconds: this.resolveDurationSeconds(durationSource, eventArgs[2])
    });
  }

  handleAudioPlayerStop(args) {
    const eventArgs = this.eventArgs(args);
    const trackData = eventArgs[0];
    const playbackContext = this.playbackContext(eventArgs);
    const durationSource = this.isVariantPlayer() ? playbackContext.trackVariantData : trackData;

    if (!this.isOwnPlayback(trackData, playbackContext)) {
      if (this.state.isPlaying) {
        this.resetPlaybackState();
      }
      return;
    }

    let currentTime = this.numericValue(eventArgs[1]);
    if (this.expectedCurrentTimeAfterSeek !== null) {
      currentTime = this.expectedCurrentTimeAfterSeek;
    }
    if (this.awaitingFreshPlaybackStart && playbackContext.seekRatio === null) {
      currentTime = 0;
    }

    this.setState({
      isActiveTrack: true,
      isPlaying: false,
      currentTime: currentTime,
      durationSeconds: this.resolveDurationSeconds(durationSource, eventArgs[2])
    });
  }

  handleAudioPlayerEnded(args) {
    const eventArgs = this.eventArgs(args);
    const trackData = eventArgs[0];
    const playbackContext = this.playbackContext(eventArgs);
    const durationSource = this.isVariantPlayer() ? playbackContext.trackVariantData : trackData;

    if (!this.isOwnPlayback(trackData, playbackContext)) {
      return;
    }

    this.setState({
      isActiveTrack: true,
      isPlaying: false,
      currentTime: 0,
      durationSeconds: this.resolveDurationSeconds(durationSource, eventArgs[2])
    });
    this.awaitingFreshPlaybackStart = false;
    this.expectedCurrentTimeAfterSeek = null;
  }

  handlePlayPauseClick(event) {
    event.preventDefault();

    if (!legacyEventHub || typeof legacyEventHub.trigger !== 'function') {
      return;
    }

    if (this.isVariantPlayer()) {
      legacyEventHub.trigger('playTrackVariant', this.props.trackVariantId, this.props.trackId);
      return;
    }

    legacyEventHub.trigger('playTrack', this.props.trackId, this.previewTrackData());
  }

  handleWaveformClick(event) {
    event.preventDefault();

    if (!legacyEventHub || typeof legacyEventHub.trigger !== 'function') {
      return;
    }

    const rect = event.currentTarget.getBoundingClientRect();
    if (!rect.width) {
      return;
    }

    const ratio = (event.clientX - rect.left) / rect.width;
    const durationSeconds = this.state.durationSeconds > 0 ? this.state.durationSeconds : this.initialDurationSeconds();
    const expectedCurrentTimeAfterSeek = durationSeconds > 0 ? durationSeconds * ratio : 0;

    this.awaitingFreshPlaybackStart = false;
    this.expectedCurrentTimeAfterSeek = expectedCurrentTimeAfterSeek;
    this.setState({
      isActiveTrack: true,
      isPlaying: false,
      currentTime: expectedCurrentTimeAfterSeek,
      durationSeconds: durationSeconds
    });

    if (this.isVariantPlayer()) {
      legacyEventHub.trigger('playTrackVariantAtRatio', {
        trackId: this.props.trackId,
        trackVariantId: this.props.trackVariantId,
        ratio: ratio
      });
      return;
    }

    legacyEventHub.trigger('playTrackAtRatio', {
      trackId: this.props.trackId,
      ratio: ratio,
      trackData: this.previewTrackData()
    });
  }

  handleWaveformKeyDown(event) {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }

    event.preventDefault();
    this.handlePlayPauseClick(event);
  }

  formatTime(totalSeconds) {
    const safeSeconds = Math.max(0, Math.floor(this.numericValue(totalSeconds)));
    const minutes = Math.floor(safeSeconds / 60);
    const seconds = safeSeconds % 60;
    return `${minutes}:${String(seconds).padStart(2, '0')}`;
  }

  progressPercent() {
    if (this.state.durationSeconds <= 0) {
      return 0;
    }

    return Math.max(0, Math.min(100, (this.state.currentTime / this.state.durationSeconds) * 100));
  }

  render() {
    const rootClassName = [
      'track-waveform-player',
      this.props.className,
      this.state.isActiveTrack ? 'track-waveform-player--active' : '',
      this.state.isPlaying ? 'track-waveform-player--playing' : ''
    ].filter(Boolean).join(' ');

    const durationLabel = this.props.durationLabel || this.formatTime(this.state.durationSeconds);
    const currentTimeLabel = this.formatTime(this.state.currentTime);
    const progressPercent = this.progressPercent();
    const inactiveStyle = this.props.waveformInactiveUrl ? { backgroundImage: `url('${this.props.waveformInactiveUrl}')` } : {};
    const playedWaveformUrl = this.props.waveformPlayedUrl || this.props.waveformInactiveUrl;
    const playedStyle = {
      ...(playedWaveformUrl ? {
        backgroundImage: `url('${playedWaveformUrl}')`
      } : inactiveStyle),
      clipPath: `inset(0 ${100 - progressPercent}% 0 0)`,
      WebkitClipPath: `inset(0 ${100 - progressPercent}% 0 0)`
    };

    return (
      <div className={rootClassName}>
        {this.props.showPlayButton ? (
          <button
            type="button"
            className="track-waveform-player__button"
            onClick={this.handlePlayPauseClick}
            aria-label={this.state.isPlaying ? this.props.i18n.pause : this.props.i18n.play}
          >
            <i className={this.state.isPlaying ? 'fa fa-pause' : 'fa fa-play'} />
          </button>
        ) : null}

        <div className="track-waveform-player__body">
          {this.props.kicker ? (
            <div className="track-waveform-player__kicker">{this.props.kicker}</div>
          ) : null}

          {this.props.title ? (
            <div className="track-waveform-player__title">{this.props.title}</div>
          ) : null}

          <div
            className="track-waveform-player__waveform"
            role="button"
            tabIndex={0}
            onClick={this.handleWaveformClick}
            onKeyDown={this.handleWaveformKeyDown}
            aria-label={this.props.i18n.seek}
          >
            <div className="track-waveform-player__waveform-layer track-waveform-player__waveform-layer--inactive" style={inactiveStyle} />
            <div className="track-waveform-player__waveform-layer track-waveform-player__waveform-layer--played" style={playedStyle} />
          </div>

          <div className="track-waveform-player__times">
            <span>{currentTimeLabel}</span>
            <span>{durationLabel}</span>
          </div>
        </div>
      </div>
    );
  }
}

TrackWaveformPlayer.defaultProps = {
  className: '',
  trackVariantId: null,
  title: '',
  kicker: '',
  waveformInactiveUrl: '',
  waveformPlayedUrl: '',
  durationLabel: '',
  durationSeconds: 0,
  showPlayButton: true,
  i18n: {
    play: 'Play preview',
    pause: 'Pause preview',
    seek: 'Seek within waveform'
  }
};

let elements = document.querySelectorAll('[data-react-component=TrackWaveformPlayer]');

elements.forEach(function(element) {
  try {
    let reactProps = parseReactProps(element.dataset.reactProps);

    ReactDOM.render(
      <React.StrictMode>
        <TrackWaveformPlayer {...reactProps} />
      </React.StrictMode>,
      element
    );
  } catch (error) {
    console.error('Failed to mount TrackWaveformPlayer', error);
  }
});

export default TrackWaveformPlayer;
